import type { Dispatch } from '@reduxjs/toolkit';
import { formatToFetchError } from 'src/helpers/utils';
import type { EarnCreditsOverviewResponse } from 'src/openapi';
import apiClient from 'src/helpers/apiClient';
import type { EarnCreditGoalKey } from 'src/pages/applicantDashboard/constants';
import appApi from './index';

const enhancedApi = appApi.enhanceEndpoints({
  addTagTypes: ['EarnCreditGoals'],
});

const applicantDashboardApi = enhancedApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    // Read-only checklist (completion computed at runtime). Goal bonuses are
    // granted by the underlying activity endpoints, not here.
    getEarnCreditsGoals: build.query<EarnCreditsOverviewResponse, void>({
      queryFn: async () => {
        try {
          const res = await (
            await apiClient.getUserApi()
          ).userApiGetEarnCreditsGoals();
          return { data: res.data };
        } catch (err: any) {
          return formatToFetchError(err);
        }
      },
      providesTags: ['EarnCreditGoals'],
    }),
  }),
});

// The enhanced api's selector wants a state whose tag map includes EarnCreditGoals,
// which the store-level RootState type doesn't carry.
type GoalsApiState = Parameters<
  ReturnType<typeof applicantDashboardApi.endpoints.getEarnCreditsGoals.select>
>[0];

/**
 * Refetch the goals checklist after an action that may have completed a goal —
 * but only when that goal is still outstanding, so the common case (applying to a
 * job when the first-application bonus was already earned) costs nothing.
 *
 * The API grants the bonus on read (compute_earn_credit_goals), so there is no
 * claimable flag on the client; an incomplete goal is the claimable signal.
 */
export const invalidateEarnCreditsGoalIfClaimable =
  (key: EarnCreditGoalKey) =>
  // getState is loosely typed so this stays dispatchable from createAsyncThunk,
  // which doesn't expose the app's own store types.
  (dispatch: Dispatch, getState: () => unknown) => {
    const { data } =
      applicantDashboardApi.endpoints.getEarnCreditsGoals.select()(
        getState() as GoalsApiState,
      );
    // No cached checklist means nothing is displaying it — it will be fetched fresh.
    if (!data || data.all_completed) {
      return;
    }
    const goal = data.goals?.find((item) => item.key === key);
    if (!goal || goal.completed) {
      return;
    }
    dispatch(applicantDashboardApi.util.invalidateTags(['EarnCreditGoals']));
  };

export const { useGetEarnCreditsGoalsQuery } = applicantDashboardApi;

export default applicantDashboardApi;
